import FrontText from "@/Components/Front/FrontText";
import FrontLayout from "@/Layouts/System/FrontLayout";
import { useTranslation } from "react-i18next";
import Container from "@/Components/Grid/Container";
import Row from "@/Components/Grid/Row";
import Column from "@/Components/Grid/Column";
import { Head, Link } from "@inertiajs/react";
import { Icon } from "@iconify/react";
import WebpImage from "@/Components/Front/Blocks/WebpImage";
import resolveLocaleRoute from "@/Helpers/resolveLocaleRoute";
import FrontSquareBeforeText from "@/Components/Front/Partials/FrontSquareBeforeText";
import { register } from "swiper/element/bundle";
import { useRef, useEffect, useState, useCallback } from "react";
import { Thumbs } from 'swiper/modules';
import AnimatedAccordion from "@/Components/Front/Blocks/Accordion/AnimatedAccordion";
import sanitizeHTML from "@/Helpers/sanitizeHTML";
import makePhoneNumber from "@/Helpers/makePhoneNumber";
import capitalizeFirstLetter from "@/Helpers/capitalizeFirstLetter";
import WarehouseOtherParam from "./WarehouseOtherParam";
import FsLightbox from "fslightbox-react";
import WarehouseSingleAdvisors from "./WarehouseSingleAdvisors";
import WarehouseContactForm from "@/Components/Front/Forms/Contact/WarehouseContactForm";
import FrontClassicButton from "@/Components/Front/Buttons/FrontClassicButton";

register();

const WarehousesSingle = ({ province, warehouse, similarWarehouses }) => {
    const { t, i18n } = useTranslation();

    const agents = warehouse.get_agents;

    let location = {
        lat: parseFloat(warehouse.lat),
        lng: parseFloat(warehouse.lng),
    };
    let zoomLevel = 12;

    /* =================== [Swiper] =================== */
    const swiperRef = useRef(null);
    const swiperThumbsRef = useRef(null);
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [realIndex, setRealIndex] = useState(1);

    const isServer = typeof window === 'undefined'

    useEffect(() => {
        const swiperContainer = swiperRef.current;
        const swiperThumbs = swiperThumbsRef.current;

        if (!thumbsSwiper) {
            const thumbsParams = {
                on: {
                    init: (swiper) => { setThumbsSwiper(swiper) }
                },
                slidesPerView: 3.2,
                freeMode: true,
                watchSlidesProgress: true,
                modules: [Thumbs],
                navigation: false,
                spaceBetween: 16,
                breakpoints: {
                    1280: {
                        slidesPerView: 5,
                    },
                    640: {
                        slidesPerView: 4,
                    }
                }
            };
            Object.assign(swiperThumbs, thumbsParams);
            swiperThumbs.initialize();
        }

        if (thumbsSwiper) {
            const params = {
                slidesPerView: 1,
                navigation: true,
                autoHeight: true,
                spaceBetween: 16,
                modules: [Thumbs],
                thumbs: { swiper: thumbsSwiper },
                on: {
                    realIndexChange: (swiper) => {
                        setRealIndex(swiper.realIndex + 1);
                    }
                },
                injectStyles: [
                    `
                        .swiper-button-next,
                        .swiper-button-prev {
                            display: none
                        }
                    `,
                ],
            };
            Object.assign(swiperContainer, params);
            swiperContainer.initialize();
        }
    }, [thumbsSwiper]);

    const handlePrev = useCallback(() => {
        if (!swiperRef.current) return;
        swiperRef.current.swiper.slidePrev();
    }, []);

    const handleNext = useCallback(() => {
        if (!swiperRef.current) return;
        swiperRef.current.swiper.slideNext();
    }, []);
    /* =================== [Swiper] =================== */

    const mobileFixedBtnClass = 'flex py-c15 justify-center items-center text-standard-black gap-c10 hover:bg-blue-classic hover:text-white transition-all ease-in-out duration-200 rounded-4';

    const Marker = ({ children }) => children;
    const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;



    let imagesSources = [];

    const [lightboxController, setLightboxController] = useState({
        toggler: false,
        slide: 1,
    });

    function openLightboxOnSlide(number) {
        setLightboxController({
            toggler: !lightboxController.toggler,
            slide: number,
        });
    }

    if (warehouse.gallery != null) {
        imagesSources.push("/storage/warehouses/" + warehouse.gallery_main);
        JSON.parse(warehouse.gallery).map((item) => {
            imagesSources.push("/storage/warehouses/" + item);
        })
    }

    const iframeRef = useRef(null);
    const iframeLink = `https://maps.google.com/maps?width=100%25&amp;height=400&amp;hl=${i18n.resolvedLanguage}&amp;q=${warehouse.lat},${warehouse.lng}&amp;t=&amp;z=15&amp;ie=UTF8&amp;iwloc=B&amp;output=embed`

    useEffect(() => {
        if (iframeRef.current) {
            iframeRef.current.src = iframeLink;
        }
    }, []);

    return (
        <FrontLayout
            breadcrumbs={[
                {
                    title: capitalizeFirstLetter(province.name),
                    href: resolveLocaleRoute('front.warehouses', { searchRegion: province.slug }, i18n.resolvedLanguage)
                },
                {
                    title: capitalizeFirstLetter(warehouse.city),
                    href: resolveLocaleRoute('front.warehouses', { searchRegion: warehouse.city_slug }, i18n.resolvedLanguage)
                },
                { title: warehouse.name, current: true },
            ]}
        >
            <Head>
                <meta name="description" content={`Szukasz powierzchni magazynowej do wynajęcia? Sprawdź szczegóły dotyczące ${warehouse.name}`} />

                {/* =================== [OG] =================== */}
                <meta property="og:title" content={`${warehouse.name} - powierzchnia magazynowa do wynajęcia | Warehousemarket.pl`} />
                <meta property="og:description" content={`Szukasz powierzchni magazynowej do wynajęcia? Sprawdź szczegóły dotyczące ${warehouse.name}`} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://warehousemarket.pl`} />
                <meta property="og:image" content={`/storage/${warehouse.gallery_main}`} />
                <meta property="og:published_time" content={warehouse.created_at} />

                {/* =================== [Twitter] =================== */}
                <meta name="twitter:title" content={`${warehouse.name} - powierzchnia magazynowa do wynajęcia | Warehousemarket.pl`} />
                <meta name="twitter:card" content={`${warehouse.name} - powierzchnia magazynowa do wynajęcia | Warehousemarket.pl`} />
                <meta name="twitter:description" content={`Szukasz powierzchni magazynowej do wynajęcia? Sprawdź szczegóły dotyczące ${warehouse.name}`} />
                <meta name="twitter:image" content={`/storage/${warehouse.gallery_main}`} />

                {/* =================== [Canonical] =================== */}
                {!isServer &&
                    <link rel="canonical" href={`${window.location.protocol}//${window.location.hostname}${window.location.pathname}`} />
                }

                {/* =================== [Title] =================== */}
                <title>{`${warehouse.name} - powierzchnia magazynowa do wynajęcia | Warehousemarket.pl`}</title>
            </Head>
            {!isServer &&
                <FsLightbox
                    toggler={lightboxController.toggler}
                    sources={imagesSources}
                    slide={lightboxController.slide}
                />
            }
            {/* =================== [Mobile Fixed] =================== */}
            <div className="fixed bottom-4 w-full z-10 sm:hidden block">
                <Container>
                    <Row>
                        <Column>
                            <div className="relative flex bg-yellow w-full rounded-4">
                                <span className="absolute w-px h-[calc(100%_-_1.87500rem)] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-standard-black"></span>
                                <div className="w-1/2">
                                    <a href={agents[0]?.phone ? makePhoneNumber(agents[0].phone) : '#advisors'} title={t('general.call')} className={mobileFixedBtnClass}>
                                        <Icon icon={'material-symbols:call'} height={20} width={20} />
                                        <FrontText textSize={'16/20'}>
                                            <span>{t('general.call')}</span>
                                        </FrontText>
                                    </a>
                                </div>
                                <div className="w-1/2">
                                    <a href={`#contact`} title={t('general.write')} className={mobileFixedBtnClass}>
                                        <Icon icon={'ic:outline-email'} height={20} width={20} />
                                        <FrontText textSize={'16/20'}>
                                            <span>{t('general.write')}</span>
                                        </FrontText>
                                    </a>
                                </div>
                            </div>
                        </Column>
                    </Row>
                </Container>
            </div>

            <Container className="mt-10">
                <Row className='gap-y-10'>
                    <Column xl={6} lg={6} md={6} className='md:pr-0'>
                        <div className="relative">
                            <swiper-container
                                ref={swiperRef}
                                init="false"
                            >
                                <swiper-slide key={-1}>
                                    <div className="flex items-center w-full">
                                        <WebpImage
                                            className="w-full mb-5 rounded-lg cursor-pointer"
                                            sources={[
                                                {
                                                    width: 1,
                                                    path: `/storage/warehouses/${warehouse.gallery_main.substring(0, warehouse.gallery_main.lastIndexOf('.'))}-700`,
                                                    mime: warehouse.gallery_main.split('.').pop(),
                                                },
                                            ]}
                                            alt={warehouse.name + ' main'}
                                            fallback={`/storage/warehouses/${warehouse.gallery_main}`}
                                            seo={{
                                                width: 660,
                                                height: 430,
                                            }}
                                            onClick={() =>
                                                openLightboxOnSlide(
                                                    realIndex
                                                )
                                            }
                                        />
                                    </div>
                                </swiper-slide>
                                {warehouse.gallery != null && JSON.parse(warehouse.gallery).map((elem, index) => (
                                    <swiper-slide key={index}>
                                        <div className="flex items-center w-full">
                                            <WebpImage
                                                className="w-full mb-5 rounded-lg cursor-pointer"
                                                sources={[
                                                    {
                                                        width: 1,
                                                        path: `/storage/warehouses/${elem.substring(0, elem.lastIndexOf('.'))}-700`,
                                                        mime: elem.split('.').pop(),
                                                    },
                                                ]}
                                                alt={warehouse.name + ' ' + index}
                                                fallback={`/storage/warehouses/${elem}`}
                                                onClick={() =>
                                                    openLightboxOnSlide(
                                                        realIndex
                                                    )
                                                }
                                                seo={{
                                                    width: 660,
                                                    height: 430,
                                                }}
                                            />
                                        </div>
                                    </swiper-slide>
                                ))}
                            </swiper-container>

                            <div className="absolute select-none z-c1 text-center bg-[rgba(40,40,40,0.68)] md:bottom-10 bottom-7 left-1/2 -translate-x-1/2 transform md:w-24 w-20 md:py-c10 py-2">
                                <FrontText textSize='14/17' className="text-white font-medium">
                                    {realIndex} / {JSON.parse(warehouse.gallery).length + 1}
                                </FrontText>
                            </div>

                            <button
                                id="prev"
                                onClick={handlePrev}
                                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-c1 p-4 bg-[rgba(48,84,138,0.47)] text-white hover:bg-[rgba(48,84,138,0.87)] transition-all ease-in-out duration-300"
                                title={t('warehouses.prevSlide')}
                            >
                                <div className="transition-all ease-in-out duration-200">
                                    <Icon
                                        icon="mingcute:left-fill"
                                        width={24}
                                        height={24}
                                    />
                                </div>
                            </button>

                            <button
                                id="next"
                                onClick={handleNext}
                                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-c1 p-4 bg-[rgba(48,84,138,0.47)] text-white hover:bg-[rgba(48,84,138,0.87)] transition-all ease-in-out duration-300"
                                title={t('warehouses.nextSlide')}
                            >
                                <div className="transition-all ease-in-out duration-200">
                                    <Icon
                                        icon="mingcute:right-fill"
                                        width={24}
                                        height={24}
                                    />
                                </div>
                            </button>
                        </div>

                        {/* =================== [Thumbs] =================== */}
                        <div className="pr-4">
                            <swiper-container
                                ref={swiperThumbsRef}
                                init="false"
                            >

                                <swiper-slide key={-1}>
                                    <div className="flex items-center">
                                        <WebpImage
                                            className="sm:h-c86 w-full h-19 object-cover object-center rounded-lg cursor-pointer"
                                            sources={[
                                                {
                                                    width: 1,
                                                    path: `/storage/warehouses/${warehouse.gallery_main.substring(0, warehouse.gallery_main.lastIndexOf('.'))}-700`,
                                                    mime: warehouse.gallery_main.split('.').pop(),
                                                },
                                            ]}
                                            alt={warehouse.name + ' main'}
                                            fallback={`/storage/warehouses/${warehouse.gallery_main}`}
                                            seo={{
                                                width: 660,
                                                height: 430,
                                            }}
                                        />
                                    </div>
                                </swiper-slide>
                                {warehouse.gallery != null && JSON.parse(warehouse.gallery).map((elem, index) => (
                                    <swiper-slide key={index}>
                                        <div className="flex items-center">
                                            <WebpImage
                                                className="sm:h-c86 w-full h-19 object-cover object-center rounded-lg cursor-pointer"
                                                sources={[
                                                    {
                                                        width: 1,
                                                        path: `/storage/warehouses/${elem.substring(0, elem.lastIndexOf('.'))}-700`,
                                                        mime: elem.split('.').pop(),
                                                    },
                                                ]}
                                                alt={warehouse.name + ' ' + index}
                                                fallback={`/storage/warehouses/${elem}`}
                                                seo={{
                                                    width: 660,
                                                    height: 430,
                                                }}
                                            />
                                        </div>
                                    </swiper-slide>
                                ))}
                            </swiper-container>
                        </div>
                    </Column>
                    <Column xl={6} lg={6} md={6} className='md:pl-0'>
                        <div className="xl:px-13 lg:px-11 md:px-8 px-0">
                            <FrontText
                                textSize="14/17"
                                className="uppercase text-standard-black"
                            >
                                <p>{t('warehouses.singleHeading', { city: warehouse.city })}</p>
                            </FrontText>
                            <FrontText
                                textSize="32/39"
                                className="text-heading-blue font-bold mb-c14"
                            >
                                <h1>{warehouse.name}</h1>
                            </FrontText>
                        </div>

                        <div className="xl:py-7 lg:py-8 md:py-6 py-5 xl:px-13 lg:px-11 md:px-8 px-5 md:mx-0 -mx-5 bg-dark-blue-standard flex flex-col gap-4 text-white">
                            <div className="flex justify-between gap-4">
                                <FrontText textSize="14/17">
                                    <p>{t("warehouses.singleLocation")}</p>
                                </FrontText>

                                <FrontText
                                    textSize="14/17"
                                    className="text-right font-bold"
                                >
                                    <p>
                                        <span className="capitalize">{province.name}, </span>
                                        <span className="capitalize">{warehouse.city}</span>
                                    </p>
                                </FrontText>
                            </div>


                            <div className="flex justify-between gap-4">
                                <FrontText textSize="14/17">
                                    <p>
                                        {t("warehouses.singleTotalExistingArea")}
                                    </p>
                                </FrontText>

                                <FrontText
                                    textSize="14/17"
                                    className="text-right font-bold"
                                >
                                    <p>
                                        {warehouse.total_area_building} m<sup>2</sup>
                                    </p>
                                </FrontText>
                            </div>

                            <div className="flex justify-between gap-4">
                                <FrontText textSize="14/17">
                                    <p>
                                        {t(
                                            "warehouses.singleTotalPlannedArea"
                                        )}
                                    </p>
                                </FrontText>

                                <FrontText
                                    textSize="14/17"
                                    className="text-right font-bold"
                                >
                                    <p>
                                        {warehouse.underconstruction_area_building} m<sup>2</sup>
                                    </p>
                                </FrontText>
                            </div>

                            <div className="flex justify-between gap-4">
                                <FrontText textSize="14/17" className="w-1/2">
                                    <p>
                                        {t(
                                            "warehouses.ctaBoxText"
                                        )}
                                    </p>
                                </FrontText>

                                <Link
                                    href="#contact"
                                    title={t("warehouses.contactAdvisor")}
                                >
                                    <FrontClassicButton className="uppercase">
                                        {t('warehouses.ctaContactText')}
                                    </FrontClassicButton>
                                </Link>
                            </div>
                        </div>

                        <section id="advisors">
                            <WarehouseSingleAdvisors
                                agents={agents}
                                currentLang={i18n.resolvedLanguage}
                            />
                        </section>


                    </Column>
                </Row>

                <Row className="justify-between md:mt-c90 mt-15 md:gap-y-15 md:mb-c90">
                    <Column xl={5} lg={5} md={6}>
                        <FrontText
                            textSize="20/24"
                            className="mb-10 font-bold text-heading-blue"
                        >
                            {t("warehouses.singleDescription")}
                        </FrontText>

                        {/* =================== [Long desc] =================== */}
                        <AnimatedAccordion buttonText={t('warehouses.expand')} expandedButtonText={t('warehouses.collapse')}>
                            <FrontText
                                spacings
                                textSize="16/20-m-same"
                                className="text-standard-black"
                            >
                                {sanitizeHTML(
                                    i18n.resolvedLanguage === 'pl'
                                        ?
                                        warehouse.get_description.description_long.replace(/\n/g, '<br/>')
                                        :
                                        warehouse.get_description.description_long_en.replace(/\n/g, '<br/>')
                                )
                                }
                            </FrontText>
                        </AnimatedAccordion>
                        {/* =================== [Long Desc] =================== */}
                    </Column>
                    {warehouse.get_other_parameters.length > 0 &&
                        <Column xl={6} lg={6} md={6}>
                            <section id="other-params">
                                <AnimatedAccordion buttonText={t('warehouses.seeMore')} expandedButtonText={t('warehouses.collapse')}>
                                    <Row className="justify-center">
                                        <Column xl={12} lg={12} md={12}>
                                            <FrontText
                                                textSize="20/24"
                                                className="text-heading-blue font-domine font-bold text-left mb-10"
                                            >
                                                {t("warehouses.singleInfrastructureStandard")}
                                            </FrontText>
                                            <div className="gap-y-6 grid xl:gap-x-10 lg:gap-x-14 md:gap-x-8 sm:gap-x-6 gap-x-5 md:grid-cols-4 sm:grid-cols-3 grid-cols-2">
                                                {warehouse.get_other_parameters.map((elem, index) => (
                                                    <WarehouseOtherParam param={elem} lang={i18n.resolvedLanguage} key={index + elem.name} />
                                                ))}
                                            </div>
                                        </Column>
                                    </Row>
                                </AnimatedAccordion>
                            </section>
                        </Column>
                    }
                </Row>
            </Container>

            <section id="contact" className="mb-30">
                <Container>
                    <Row className="justify-between">
                        <Column xl={5} lg={6} md={12} className={'lg:mb-0 mb-15'}>
                            <FrontText
                                textSize="20/24"
                                className="mb-10 font-bold text-heading-blue"
                            >
                                {t("warehouses.singleLocation")}
                            </FrontText>

                            <div className="h-[400px]">
                                <iframe
                                    className="w-full h-[400px] border-none"
                                    loading="lazy"
                                    src={`https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${warehouse.lat},${warehouse.lng}`}
                                >
                                </iframe>
                            </div>
                        </Column>
                        <Column xl={6} lg={6} md={12} className="relative">
                            <FrontText
                                textSize="20/24"
                                className="mb-10 font-bold text-heading-blue"
                            >
                                {t("warehouses.ctaContactText")}
                            </FrontText>
                            <WarehouseContactForm referer={`Magazyn - ${warehouse.name}`} />
                        </Column>
                    </Row>
                </Container>
            </section>

            <section id="inne-magazyny" className="my-20 ">
                <Container>
                    <Row>
                        <Column>
                            <FrontSquareBeforeText className="mb-8">
                                {t("warehouses.singleViewOtherWarehouses")}
                            </FrontSquareBeforeText>
                        </Column>
                    </Row>

                    <Row className='gap-y-10'>
                        {similarWarehouses.map((elem, index) => (
                            <Column xl={3} lg={4} md={6} sm={6} key={index}>
                                <article className="flex h-full flex-col">
                                    <div className="w-full border-transparent border border-b-0">
                                        <Link href={resolveLocaleRoute('front.warehouses.single', { province: elem.get_region.slug, slug: elem.slug }, i18n.resolvedLanguage)} title={t('alts.showDetails') + ' - ' + elem.name}>
                                            <WebpImage
                                                className="rounded-t-10 h-[200px] w-full object-cover"
                                                sources={[
                                                    {
                                                        width: 1,
                                                        path: `/storage/warehouses/${elem.gallery_main.substring(0, elem.gallery_main.lastIndexOf('.'))}-700`,
                                                        mime: elem.gallery_main.split('.').pop(),
                                                    },
                                                ]}
                                                alt={elem.name + ' main'}
                                                fallback={`/storage/warehouses/${elem.gallery_main}`}
                                                seo={{
                                                    width: 660,
                                                    height: 430,
                                                }}
                                                lazy
                                            />
                                        </Link>
                                    </div>
                                    <div className="w-full h-full flex flex-col border-E0E0E0 border border-t-0 p-c18 rounded-t-none bg-hover transition-all ease-in-out duration-300 text-start">
                                        <FrontText
                                            textSize="20/24"
                                            className="font-bold text-heading-blue mb-3 hover-white transition-all ease-in-out duration-300"
                                            customLinkStyles
                                        >
                                            <Link href={resolveLocaleRoute('front.warehouses.single', { province: elem.get_region.slug, slug: elem.slug }, i18n.resolvedLanguage)} title={t('alts.showDetails') + ' - ' + elem.name} className="hover:text-light-yellow transition-all ease-in-out duration-300">
                                                {elem.name}
                                            </Link>
                                        </FrontText>

                                        <div className="flex items-center gap-x-c10 shrink-0 mb-6 mt-auto">
                                            <svg
                                                width="18"
                                                height="26"
                                                viewBox="0 0 18 26"
                                                fill="t"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <g>
                                                    <g>
                                                        <ellipse
                                                            cx="9"
                                                            cy="24"
                                                            rx="7"
                                                            ry="2"
                                                            fill="#30548A"
                                                        />
                                                        <g>
                                                            <g>
                                                                <ellipse
                                                                    cx="8.99962"
                                                                    cy="7.875"
                                                                    rx="8.21739"
                                                                    ry="7.875"
                                                                    fill="#30548A"
                                                                />
                                                                <path
                                                                    d="M9 24L1.20577 10.5L16.7942 10.5L9 24Z"
                                                                    fill="#30548A"
                                                                />
                                                            </g>
                                                            <circle
                                                                className="hover-change transition-all duration-300 ease-in-out"
                                                                cx="9"
                                                                cy="7.5"
                                                                r="4.5"
                                                                fill="white"
                                                            />
                                                        </g>
                                                    </g>
                                                </g>
                                            </svg>
                                            <FrontText
                                                textSize="16/20"
                                                className="text-standard-black font-medium hover-white transition-all ease-in-out duration-300"
                                                customLinkStyles
                                            >
                                                <Link href={resolveLocaleRoute('front.warehouses', { searchRegion: elem.city_slug ? elem.city_slug : i18n.resolvedLanguage == 'pl' ? elem.get_region.slug : elem.get_region.slug_en }, i18n.resolvedLanguage)} className="hover:text-yellow transition-all ease-in-out duration-200">
                                                    <span>{elem.city ? elem.city : elem.get_region.name}</span>
                                                </Link>
                                            </FrontText>
                                        </div>

                                        <FrontText
                                            textSize="14/17"
                                            className="text-standard-black hover-white transition-all ease-in-out duration-300"
                                        >
                                            <p>
                                                {t(
                                                    "warehouses.rentAndTotalArea"
                                                )}
                                            </p>
                                        </FrontText>

                                        <Link
                                            rel="nofollow"
                                            href={resolveLocaleRoute('front.warehouses.single', {
                                                province: elem.get_region.slug,
                                                slug: elem.slug
                                            }, i18n.resolvedLanguage) + '#advisors'}
                                            title={t(
                                                "warehouses.contactAdvisor"
                                            )}
                                            className="mt-2 block transition-all ease-in-out duration-300 text-heading-blue hover:!text-yellow hover-white"
                                        >
                                            <FrontText textSize="16/20" className="font-semibold">
                                                <span>
                                                    {t(
                                                        "warehouses.contactAdvisor"
                                                    )}
                                                </span>
                                            </FrontText>
                                        </Link>
                                    </div>

                                    <Link rel="nofollow" href={resolveLocaleRoute('front.warehouses.single', {
                                        province: elem.get_region.slug,
                                        slug: elem.slug
                                    }, i18n.resolvedLanguage)} title={t('alts.showDetails') + ' - ' + elem.name} className="bg-light-blue text-white hover:bg-blue-classic block transition-all ease-in-out duration-300 rounded-b-10">
                                        <div className="gap-[10px] flex items-center px-5 lg:py-c10 py-2 justify-center">
                                            <FrontText
                                                textSize={"16/20-m-14/17"}
                                                className="font-semibold"
                                            >
                                                <span>{t('alts.showDetails')}</span>
                                            </FrontText>


                                            <Icon
                                                icon={"material-symbols:arrow-right-alt"}
                                                width={24}
                                                height={24}
                                            />
                                        </div>
                                    </Link>

                                </article>
                            </Column>
                        ))}
                    </Row>
                </Container>
            </section>
        </FrontLayout>
    );
};

export default WarehousesSingle;
